/**
 * Pestaña de Candidatos para la vista de detalle de Job Request
 * Muestra todos los candidatos que han aplicado a la vacante
 */

import { useState, useEffect } from 'react'
import { User, XCircle, Search } from 'lucide-react'
import FilterSelect, { type FilterOption } from '@/shared/components/FilterSelect'
import { esRecomendacionPendiente } from '@/features/rh/lib/origenDelCandidato'

/** Valor del filtro de recomendaciones; no es un estatus del tablero. */
const PENDIENTES_DE_VINCULAR = 'pendientes'
import { CandidatoStatusBadge } from '@/features/rh/components/CandidatoStatusBadge'
import { estiloDeEstatus } from '@/features/rh/lib/estatusDeCandidato'
import { apiService } from '@/services/api'
import type { Candidate } from '@/services/api'

interface JobRequestCandidatesTabProps {
  jobRequestId: number
}

export const JobRequestCandidatesTab = ({ jobRequestId }: JobRequestCandidatesTabProps) => {
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Los dos filtros del diseño, sobre lo ya cargado: son los candidatos de una
  // sola vacante, no hace falta volver a pedirlos al servidor.
  const [busqueda, setBusqueda] = useState('')
  const [estatus, setEstatus] = useState('')
  const [aprobadoPor, setAprobadoPor] = useState('')
  const [recomendaciones, setRecomendaciones] = useState('')

  useEffect(() => {
    loadCandidates()
  }, [jobRequestId])

  const loadCandidates = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await apiService.getCandidatesByJobRequest(jobRequestId)
      setCandidates(data)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error al cargar candidatos')
    } finally {
      setIsLoading(false)
    }
  }

  // Obtener el nombre de "Aprobado por" basado en el estado
  const getApprovedBy = (candidate: Candidate) => {
    // Lógica simplificada - puedes expandir esto según tus necesidades
    if (
      candidate.status === 'entrevista_rh' ||
      candidate.status === 'pre_seleccionado' ||
      candidate.status === 'entrevista_solicitante' ||
      candidate.status === 'seleccionado' ||
      candidate.status === 'contratado'
    ) {
      return 'Recursos Humanos'
    }
    return '-'
  }

  const etiquetaDeEstatus = (status: string) =>
    estiloDeEstatus(status).label

  const candidatosFiltrados = candidates.filter((c) => {
    const texto = busqueda.trim().toLowerCase()
    if (texto) {
      const campos = [c.fullName, c.email, c.phone]
        .map((v) => (v ?? '').toLowerCase())
        .join(' ')
      if (!campos.includes(texto)) return false
    }

    if (estatus && etiquetaDeEstatus(c.status) !== estatus) return false
    if (aprobadoPor && getApprovedBy(c) !== aprobadoPor) return false
    if (
      recomendaciones === PENDIENTES_DE_VINCULAR &&
      !esRecomendacionPendiente(c)
    ) {
      return false
    }

    return true
  })

  /**
   * El filtro de recomendaciones (RH-06). Es una sola opcion y no una lista
   * derivada: lo que se busca es el pendiente, no repasar cada origen.
   */
  const hayPendientes = candidates.some(esRecomendacionPendiente)
  const OPCIONES_DE_RECOMENDACION: FilterOption[] = [
    { value: PENDIENTES_DE_VINCULAR, label: 'Pendientes de vincular' },
  ]

  /**
   * Los estatus que de verdad aparecen en esta vacante, con la etiqueta de la
   * pastilla. Derivarlos evita el desfase de una lista fija: el día que el
   * tablero renombre una columna, el filtro la sigue sola.
   */
  const OPCIONES_DE_ESTATUS: FilterOption[] = [
    ...new Set(candidates.map((c) => etiquetaDeEstatus(c.status))),
  ]
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((v) => ({ value: v, label: v }))

  /** Quien aprobó, tomado de los candidatos que hay y no de un catálogo. */
  const opcionesDeAprobador: FilterOption[] = [
    ...new Set(
      candidates.map((c) => getApprovedBy(c)).filter((v) => v !== '-'),
    ),
  ]
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((v) => ({ value: v, label: v }))

  const hayFiltros =
    busqueda !== '' ||
    estatus !== '' ||
    aprobadoPor !== '' ||
    recomendaciones !== ''

  const limpiarFiltros = () => {
    setBusqueda('')
    setEstatus('')
    setAprobadoPor('')
    setRecomendaciones('')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando candidatos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-900 mb-2">Error</h3>
        <p className="text-red-700">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Barra de filtros, en su contenedor blanco como en los listados del
          resto de la app. Sustituye al titulo y al contador dobles que habia:
          el conteo se queda a la derecha, que es donde lo pone el diseño. */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E0E0E0] bg-surface p-4">
        <div className="relative w-full max-w-[320px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en candidatos..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full rounded-lg py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
          />
        </div>

        <FilterSelect
          className="w-[190px]"
          label="Estatus"
          allLabel="Todos los estatus"
          options={OPCIONES_DE_ESTATUS}
          value={estatus}
          onChange={setEstatus}
        />

        <FilterSelect
          className="w-[190px]"
          label="Aprobado por"
          allLabel="Todos"
          options={opcionesDeAprobador}
          value={aprobadoPor}
          onChange={setAprobadoPor}
        />

        {/*
          Solo se ofrece si hay algo que filtrar: un desplegable que siempre
          deja la lista igual se lee como que esta roto.
        */}
        {hayPendientes && (
          <FilterSelect
            className="w-[210px]"
            label="Recomendaciones"
            allLabel="Todas"
            options={OPCIONES_DE_RECOMENDACION}
            value={recomendaciones}
            onChange={setRecomendaciones}
          />
        )}

        <button
          type="button"
          onClick={limpiarFiltros}
          disabled={!hayFiltros}
          title="Limpiar filtros"
          className="px-1 text-xs font-bold underline underline-offset-2 text-[#477FFA] transition-colors hover:text-[#2748B8] disabled:cursor-default disabled:text-[#B5B5B5] disabled:no-underline"
        >
          Limpiar
        </button>

        <span className="ml-auto text-sm font-medium text-[#477FFA]">
          {candidatosFiltrados.length}{' '}
          {candidatosFiltrados.length === 1 ? 'Candidato' : 'Candidatos'}
        </span>
      </div>

      {/* Tabla de candidatos */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        {candidatosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay candidatos
            </h3>
            <p className="text-gray-600">
              Aún no se han registrado candidatos para esta vacante.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Candidato
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Correo de contacto
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Número de contacto
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Aprobado por
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estatus
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {candidatosFiltrados.map((candidate) => (
                  <tr key={candidate.id} className="hover:bg-[#F4F7FB] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {candidate.fullName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {candidate.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {candidate.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getApprovedBy(candidate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <CandidatoStatusBadge status={candidate.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
